/**
 * PostHog Cloud integration — the behavioral-analytics + session-replay track
 * (see ADR 0002 and docs/explanation/conversion-funnel-analytics.md). Distinct
 * from the first-party DIY funnel: PostHog owns autocapture + replay; our owned
 * milestone funnel stays in Postgres. Both key off the SAME `tenders_vid`, so a
 * person is one identity across DIY funnel + Meta + PostHog.
 *
 * DORMANT unless `VITE_POSTHOG_KEY` is set at build time — no SDK is loaded and
 * nothing is captured. The SDK is dynamic-imported (lazy, non-blocking) so it
 * never weighs down the initial mobile bundle.
 */
import type { PostHog } from 'posthog-js';
import { parseWebEnv } from './env';
import { getOrCreateVisitorId, rotateVisitorId } from './meta-cookies';
import { isInternalViewer } from './internal-viewer';

/** Matches our project's region (US Cloud, project 519110). Override via
 *  VITE_POSTHOG_HOST if the project ever moves region. */
const DEFAULT_HOST = 'https://us.i.posthog.com';

let phPromise: Promise<PostHog | null> | null = null;
/**
 * The RESOLVED client, kept alongside the promise.
 *
 * Everything else here is fire-and-forget and can await `phPromise`, but a
 * feature flag is read during render and must answer synchronously — a promise
 * cannot. Null until the lazy import lands, which is precisely why
 * `phFeatureFlag` treats "not yet" and "no PostHog" identically: both mean the
 * variant has not been assigned, so both must render control.
 */
let phClient: PostHog | null = null;

function apiKey(): string | undefined {
  try {
    return parseWebEnv(import.meta.env).VITE_POSTHOG_KEY;
  } catch {
    return undefined;
  }
}

/**
 * Lazily initialise PostHog. Safe to call more than once (idempotent) and on
 * SSR (no-ops). No-op when no key is configured. Call once, post-hydration.
 */
export function initPostHog(): void {
  if (typeof window === 'undefined') return;
  const key = apiKey();
  if (!key || phPromise) return;

  const host = (() => {
    try {
      return parseWebEnv(import.meta.env).VITE_POSTHOG_HOST ?? DEFAULT_HOST;
    } catch {
      return DEFAULT_HOST;
    }
  })();

  phPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      phClient = posthog;
      posthog.init(key, {
        api_host: host,
        // When api_host is our first-party reverse proxy (https://uztender.com/ingest,
        // set in CI to dodge ad blockers that blocklist *.posthog.com), the SDK can't
        // infer where the PostHog app itself lives — point the toolbar / "view in
        // PostHog" links at the real US console. Harmless when api_host is direct.
        ui_host: 'https://us.posthog.com',
        // Modern defaults (SPA pageviews on history change, sensible session
        // recording — strictMinimumDuration + canvas capture). Matches the
        // version PostHog suggested for this project.
        defaults: '2026-05-30',
        // Only create person profiles for identified (logged-in) users; anonymous
        // events still capture but don't spawn a profile — privacy + cost.
        person_profiles: 'identified_only',
        autocapture: true,
        capture_pageview: 'history_change',
        /*
         * JS error tracking. `$exception` was 0 for 90 days — not because the app
         * throws nothing, but because exception capture was off
         * (`$exception_capture_enabled_server_side: false`), leaving us blind to
         * client crashes exactly as we scale ad traffic. Enabled here client-side,
         * which the SDK honours over the remote flag. The object form (not a bare
         * `true`) turns on unhandled errors + promise rejections but NOT console
         * errors — those are noisy and mostly third-party. The PostHog project's
         * Error Tracking product still has to be enabled to VIEW them; capture
         * flows regardless.
         */
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        /*
         * Session replay with masking: never record input values, and mask the
         * text of anything tagged `.ph-mask` / `[data-ph-mask]` (the revealed
         * заказчик contact). `.ph-no-capture` blocks an element entirely.
         *
         * `sampleRate` is set explicitly because this object REPLACES what
         * `defaults: '2026-05-30'` derives rather than merging into it. The
         * live remote config showed `sampleRate: null` — every session recorded
         * end to end, including every bot that clears PostHog's own UA filter.
         *
         * A client-side `sampleRate` takes precedence over the project setting
         * ("When set, takes precedence over the remote config" — the SDK
         * resolves it as `client ?? remote`), so this is the single source of
         * truth. It is deliberately low: the sessions actually worth watching
         * are opted back in at full rate by `phStartRecording()` (see below),
         * which beats paying to record every bounce on the off-chance one of
         * them converts.
         *
         * ⚠️ `strictMinimumDuration` ENFORCES NOTHING ON ITS OWN, and is kept
         * only so a floor works correctly if one is ever configured. It changes
         * HOW the minimum is compared (against buffered data rather than wall
         * clock), but the minimum ITSELF is remote-only:
         * `minimumDurationMilliseconds` does not exist in the client config
         * type at all, and the recorder reads it from
         * `$session_recording_remote_config`, which is currently `null`. A real
         * duration floor has to be set in PostHog project settings — it cannot
         * be shipped from here.
         */
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '.ph-mask, [data-ph-mask]',
          sampleRate: 0.1,
          strictMinimumDuration: true,
        },
      });
      /*
       * Carry the shared visitor id as a SUPER PROPERTY, not as
       * `bootstrap.distinctID`.
       *
       * Bootstrapping it hijacked PostHog's own identity. In the installed SDK
       * both bootstrap guards are gated on `isIdentifiedID`, which we did not
       * set, so every init took the unguarded branch and force-wrote
       * `distinct_id = vid` plus `$user_state = 'anonymous'` — even for an
       * already-identified user — firing a redundant `$identify` on every full
       * page load.
       *
       * Setting `isIdentifiedID: true` is NOT the fix: PostHog's docs say to
       * set it false "if you're generating a random anonymous ID", which is
       * exactly what `tenders_vid` is. True would mark every anonymous visitor
       * as identified and spawn a person profile per visitor, defeating
       * `person_profiles: 'identified_only'` above.
       *
       * As a super property the id rides on every event, so the DIY funnel and
       * Meta still join to PostHog — without touching PostHog's identity.
       */
      const vid = getOrCreateVisitorId();
      if (vid) posthog.register({ tenders_vid: vid });
      return posthog;
    })
    .catch(() => null);
}

/**
 * Staff / test sessions: stop capturing entirely (events, replays, autocapture).
 * Opt back in only if this browser had been opted out — the default state is
 * opted-in, and an unconditional `opt_in_capturing()` would emit an `$opt_in`
 * event on every ordinary login. See `setInternalViewer` in tracking.ts for the
 * Meta/GA half and the localStorage memory that also covers anonymous loads.
 */
export function phSetInternal(internal: boolean): void {
  if (!phPromise) return;
  void phPromise.then((p) => {
    if (!p) return;
    if (internal) p.opt_out_capturing();
    else if (p.has_opted_out_capturing()) p.opt_in_capturing();
  });
}

/** Link the current (anonymous) session to a user on login/register. */
export function phIdentify(userId: string, props?: Record<string, unknown>): void {
  if (!phPromise) return;
  void phPromise.then((p) => p?.identify(userId, props));
}

/**
 * Detach the identity on logout (starts a fresh anonymous session).
 *
 * Rotates `tenders_vid` too, and does so even when PostHog is dormant: the id
 * is shared with Meta's `external_id` and the DIY funnel's `anon_id`, so
 * leaving it in place would let the next person to log in on this browser be
 * merged with the one who just left. See `rotateVisitorId`.
 */
export function phReset(): void {
  rotateVisitorId();
  if (!phPromise) return;
  void phPromise.then((p) => {
    p?.reset();
    // `reset()` clears super properties as well, so re-register the NEW id —
    // otherwise every post-logout event ships without the join key.
    const vid = getOrCreateVisitorId();
    if (vid) p?.register({ tenders_vid: vid });
  });
}

/**
 * Force this session to be recorded regardless of the 10% sample.
 *
 * Sampling is the right default for a catalog people bounce off, but it is the
 * wrong default for the handful of sessions we most need to watch: over a
 * fortnight there were 39 `checkout_started` against ~27k sessions, so a flat
 * 10% sample would capture roughly four of them — too few to see a pattern in
 * the step where most purchase intent is lost.
 *
 * Call this at the moment a session becomes high-value (checkout). Recording
 * starts from the call onward, not retroactively, which is the trade for not
 * paying to record everything.
 */
export function phStartRecording(): void {
  if (isInternalViewer()) return;
  if (!phPromise) return;
  /*
   * `true` is load-bearing. Called with NO argument this is a no-op against
   * sampling — verified in the shipped SDK (posthog-js@1.407.2):
   *
   *   startSessionRecording(t) {
   *     var o = (t === true),
   *         a = { sampling: o || !(t == null || !t.sampling), … };
   *     Object.values(a).some(Boolean) && ( … overrideSampling() … )
   *   }
   *
   * With no argument every flag is false, `.some(Boolean)` is false, and no
   * override runs — the call only clears `disable_session_recording`. So the
   * 10% sample still applied at checkout and ~90% of the sessions this exists
   * to capture were dropped, while the code read as though they were not.
   *
   * `true` means "ignore all ingestion controls", which is exactly the intent:
   * a checkout is the one session we always want.
   */
  void phPromise.then((p) => p?.startSessionRecording(true));
}

/** Mirror a milestone event to PostHog (no-op when dormant). */
export function phCapture(event: string, props?: Record<string, unknown>): void {
  if (!phPromise) return;
  void phPromise.then((p) => p?.capture(event, props));
}

/**
 * Report an error that a boundary CAUGHT (no-op when dormant).
 *
 * `capture_exceptions` above only sees errors that reach `window.onerror` or an
 * unhandled rejection. A render error caught by the router's `errorElement`
 * reaches neither, so without an explicit call it is invisible to Error
 * Tracking — see `lib/page-error.ts`. Never throws: the caller is an error
 * boundary, and a throw from the `.then` would surface as an unhandled
 * rejection of its own.
 */
export function phCaptureException(error: unknown, props?: Record<string, unknown>): void {
  if (!phPromise) return;
  void phPromise.then((p) => {
    try {
      p?.captureException(error, props);
    } catch {
      // reporting must never break the page
    }
  });
}

/**
 * Read a PostHog feature flag, with the CONTROL branch as the only default.
 *
 * ⚠️ Every failure mode here must land on control, and there are more of them
 * than on most sites: PostHog is loaded lazily and is dormant without a key, an
 * ad-blocker kills the script outright for a share of real users, and flags
 * arrive asynchronously after `init` — so at first paint no flag is known yet.
 * A helper that returned `undefined` in those cases would push the branching
 * decision into every caller, and the caller that got it wrong would ship a
 * variant to people the experiment never assigned.
 *
 * So: `false` unless PostHog is loaded AND has positively said the flag is on.
 * That is the same shape `phCapture` uses for the same reason — dormant is a
 * normal state here, not an error.
 *
 * `onChange` fires once flags land, because the first render happens before
 * they do. Returns an unsubscribe.
 */
export function phFeatureFlag(key: string): boolean {
  if (!phClient) return false;
  return phClient.isFeatureEnabled(key) === true;
}

/** Subscribe to flag arrival/refresh. No-op (and a no-op unsubscribe) when dormant. */
export function phOnFeatureFlags(cb: () => void): () => void {
  if (!phPromise) return () => {};
  let cancelled = false;
  let off: (() => void) | undefined;
  void phPromise.then((p) => {
    if (!p || cancelled) return;
    off = p.onFeatureFlags(() => cb());
  });
  return () => {
    cancelled = true;
    off?.();
  };
}
